import { useTracking } from '@/libs/analytics';
import { AppState, RedirectLoginOptions } from '@/libs/auth0';
import useAuth from '@/libs/auth0/use-auth';
import { getDeviceId, getSessionId } from '@amplitude/analytics-browser';
import { useCallback, useMemo, useRef } from 'react';
import { useCookieResponse } from '../use-cookie-response';

export function useLogin() {
    const { flush } = useTracking();
    const { loginWithRedirect, isLoading } = useAuth();
    const hasRunLogin = useRef(false);
    const { deviceCountry, response, isFetched } = useCookieResponse();
    const deviceId = getDeviceId();
    const sessionId = getSessionId();

    const isReady = useMemo(
        () => !isLoading && isFetched,
        [isLoading, isFetched]
    );

    const login = useCallback(
        async (opts: RedirectLoginOptions<AppState>) => {
            hasRunLogin.current = true;
            await flush();
            return loginWithRedirect({
                ...opts,
                authorizationParams: {
                    ...opts.authorizationParams,
                    // Can define custom parameters https://auth0.github.io/auth0-react/interfaces/RedirectLoginOptions.html#authorizationParams
                    ...(response ? { ['ext-tracking']: response } : {}),
                    ...(deviceCountry
                        ? { ['ext-device-country']: deviceCountry }
                        : {}),
                    ...(deviceId ? { ['ext-amp_device_id']: deviceId } : {}),
                    ...(sessionId
                        ? { ['ext-amp_session_id']: sessionId.toString() }
                        : {}),
                },
            });
        },
        [deviceCountry, deviceId, flush, loginWithRedirect, response, sessionId]
    );

    const loginIfNotRun = useCallback(
        async (opts: RedirectLoginOptions<AppState>) => {
            if (!hasRunLogin.current) {
                await login(opts);
            }
        },
        [login]
    );

    return { login, loginIfNotRun, isReady };
}
